// ShareModal — modal com as opções de compartilhamento.
// Aberto pelo ShareButton. Consome extractShareData() + ShareCardGenerator.
// Todas as ações são client-side ou usam POST /api/share/create.
//
// Design: fiel ao padrão do app (Fraunces + Manrope, navy + gold).

import React, { useEffect, useMemo, useState } from "react";
import { X, Image as ImageIcon, MessageCircle, Link2, Copy, Download, Loader2, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { extractShareData, buildWhatsappText, selectTemplateFromTheme, ALL_TEMPLATES } from "./ShareTemplateEngine";
import { generateShareCard } from "./ShareCardGenerator";
import { trackShareEvent, getHideName, setHideName } from "./ShareAnalytics";

const APP_BASE_URL = process.env.REACT_APP_BACKEND_URL || "";

async function copyToClipboard(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return true;
  }
  // fallback
  const ta = document.createElement("textarea");
  ta.value = text;
  document.body.appendChild(ta);
  ta.select();
  const ok = document.execCommand("copy");
  document.body.removeChild(ta);
  return ok;
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function ShareModal({ open, onClose, contentProps, currentUser }) {
  const [busy, setBusy] = useState(null); // "story"|"feed"|"whatsapp"|"link"|"text"|"download"
  const [hideName, setHide] = useState(getHideName());
  const [shareLink, setShareLink] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [template, setTemplate] = useState("contemplacao");
  const [templateAutoSelected, setTemplateAutoSelected] = useState(false);

  const shareData = useMemo(() => extractShareData(contentProps || {}), [contentProps]);

  // P4: auto-seleção determinística ao abrir o modal (usuário pode trocar).
  useEffect(() => {
    if (!open) return;
    const suggested = selectTemplateFromTheme(shareData);
    setTemplate(suggested);
    setTemplateAutoSelected(true);
  }, [open, shareData]);

  useEffect(() => {
    if (!open) {
      setShareLink(null);
      setPreviewUrl(null);
      setBusy(null);
      setTemplateAutoSelected(false);
    }
  }, [open]);

  useEffect(() => {
    setHideName(hideName);
  }, [hideName]);

  if (!open) return null;

  const sharedBy = hideName || !currentUser
    ? "App CM, Bíblia com Propósito"
    : (currentUser.name || currentUser.email?.split("@")[0] || "App CM");

  async function ensureShareLink() {
    if (shareLink) return shareLink;
    const payload = {
      content_type: contentProps.contentType,
      content_id: contentProps.contentId,
      share_data: shareData,
      shared_by: hideName ? "" : (currentUser?.name || null),
      template,
    };
    const { data } = await api.post("/share/create", payload);
    const url = data?.url || `${APP_BASE_URL.replace(/\/$/, "")}/share/${data.share_id}`;
    setShareLink({ id: data.share_id, url });
    return { id: data.share_id, url };
  }

  async function handleGenerateImage(format) {
    setBusy(format);
    try {
      const blob = await generateShareCard({ format, template, data: shareData, sharedBy });
      const objectUrl = URL.createObjectURL(blob);
      setPreviewUrl({ url: objectUrl, format, blob });
      trackShareEvent("card_generated", { format, template, contentType: contentProps.contentType });
    } catch (e) {
      toast.error("Não foi possível gerar a imagem agora.");
    } finally {
      setBusy(null);
    }
  }

  async function handleCopyText() {
    setBusy("text");
    try {
      const { url } = await ensureShareLink();
      const txt = buildWhatsappText(shareData, url);
      const ok = await copyToClipboard(txt);
      if (ok) {
        toast.success("Texto copiado, cole onde quiser.");
        trackShareEvent("text_copied", { contentType: contentProps.contentType });
      } else {
        toast.error("Copiar falhou. Tente selecionar e copiar manualmente.");
      }
    } catch (e) {
      toast.error("Não foi possível montar o texto agora.");
    } finally {
      setBusy(null);
    }
  }

  async function handleShareWhatsapp() {
    setBusy("whatsapp");
    try {
      const { url } = await ensureShareLink();
      const txt = buildWhatsappText(shareData, url);
      if (navigator.share) {
        try {
          await navigator.share({ text: txt, url });
          trackShareEvent("whatsapp_shared", { via: "web_share_api" });
          return;
        } catch (err) {
          if (err?.name === "AbortError") return;
        }
      }
      const waUrl = "https://wa.me/?text=" + encodeURIComponent(txt);
      window.open(waUrl, "_blank", "noopener,noreferrer");
      trackShareEvent("whatsapp_shared", { via: "wa_me" });
    } catch (e) {
      toast.error("Não foi possível abrir o WhatsApp agora.");
    } finally {
      setBusy(null);
    }
  }

  async function handleCopyLink() {
    setBusy("link");
    try {
      const { url } = await ensureShareLink();
      const ok = await copyToClipboard(url);
      if (ok) {
        toast.success("Link copiado.");
      } else {
        toast("Link gerado, selecione abaixo e copie manualmente.");
      }
      trackShareEvent("link_copied", { contentType: contentProps.contentType });
    } catch (e) {
      toast.error("Não foi possível gerar o link agora.");
    } finally {
      setBusy(null);
    }
  }

  async function handleDownloadImage() {
    if (!previewUrl) return;
    setBusy("download");
    try {
      const fname = `cm-${contentProps.contentType}-${Date.now()}.png`;
      downloadBlob(previewUrl.blob, fname);
      trackShareEvent("image_downloaded", { format: previewUrl.format });
    } finally {
      setBusy(null);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-navy/60 backdrop-blur-sm p-4 animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      data-testid="share-modal"
    >
      <div
        className="w-full max-w-xl bg-white rounded-2xl shadow-2xl border border-gold/30 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-6 pt-6 pb-4 border-b border-navy/10">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Compartilhar Esperança</p>
            <h2 className="mt-1 font-display text-2xl text-navy" data-testid="share-modal-title">
              Como deseja compartilhar?
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            data-testid="share-modal-close"
            className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-navy/5 text-navy/70"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-3">
          {/* Template picker — P4: mostra os 7 estilos, com badge "Sugerido" no auto-selecionado */}
          <div className="flex items-center justify-between pb-1">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-navy/60">
              <Sparkles className="w-3.5 h-3.5 text-gold" /> Estilo
            </div>
            {templateAutoSelected && (
              <span
                className="text-[10px] uppercase tracking-widest text-gold/80"
                data-testid="share-template-auto-hint"
              >
                Sugerido pelo tema
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-2 pb-2" data-testid="share-template-picker">
            {ALL_TEMPLATES.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => {
                  setTemplate(t.id);
                  setTemplateAutoSelected(false);
                }}
                data-testid={`share-template-${t.id}`}
                className={`text-xs uppercase tracking-widest rounded-full border px-3 py-1.5 transition-colors ${
                  template === t.id
                    ? "bg-navy text-white border-navy"
                    : "bg-white text-navy/70 border-navy/15 hover:border-gold"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Preview */}
          {previewUrl && (
            <div className="rounded-xl overflow-hidden border border-navy/10 bg-[hsl(var(--parchment))] p-3">
              <img
                src={previewUrl.url}
                alt="Prévia do card"
                className={previewUrl.format === "feed" ? "w-full max-w-xs mx-auto" : "w-full max-w-[220px] mx-auto"}
                data-testid="share-preview-image"
              />
              <div className="mt-3 flex justify-center">
                <button
                  type="button"
                  onClick={handleDownloadImage}
                  data-testid="share-download"
                  disabled={busy === "download"}
                  className="inline-flex items-center gap-2 rounded-full bg-navy text-white px-5 py-2 text-[11px] uppercase tracking-widest disabled:opacity-50 hover:bg-navy/90"
                >
                  {busy === "download" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                  Baixar imagem
                </button>
              </div>
            </div>
          )}

          {/* Options grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <ActionRow
              testid="share-option-story"
              icon={<ImageIcon className="w-4 h-4" />}
              label="Imagem para Stories (9:16)"
              onClick={() => handleGenerateImage("story")}
              busy={busy === "story"}
            />
            <ActionRow
              testid="share-option-feed"
              icon={<ImageIcon className="w-4 h-4" />}
              label="Imagem para Feed (1:1)"
              onClick={() => handleGenerateImage("feed")}
              busy={busy === "feed"}
            />
            <ActionRow
              testid="share-option-whatsapp"
              icon={<MessageCircle className="w-4 h-4" />}
              label="Compartilhar no WhatsApp"
              onClick={handleShareWhatsapp}
              busy={busy === "whatsapp"}
            />
            <ActionRow
              testid="share-option-link"
              icon={<Link2 className="w-4 h-4" />}
              label="Copiar link da reflexão"
              onClick={handleCopyLink}
              busy={busy === "link"}
            />
            <ActionRow
              testid="share-option-text"
              icon={<Copy className="w-4 h-4" />}
              label="Copiar texto"
              onClick={handleCopyText}
              busy={busy === "text"}
              className="sm:col-span-2"
            />
          </div>

          {/* Toggle hide name */}
          {currentUser && (
            <label
              className="flex items-center justify-between mt-2 rounded-xl border border-navy/10 px-4 py-3 text-sm text-navy/80"
              data-testid="share-toggle-hide-name"
            >
              <span>Ocultar meu nome no compartilhamento</span>
              <input
                type="checkbox"
                checked={hideName}
                onChange={(e) => setHide(e.target.checked)}
                className="w-4 h-4 accent-gold"
              />
            </label>
          )}
          {shareLink && (
            <p className="mt-2 text-[11px] text-navy/50 break-all" data-testid="share-link-text">
              Link: {shareLink.url}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function ActionRow({ icon, label, onClick, busy, className = "", testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      data-testid={testid}
      className={`inline-flex items-center gap-3 rounded-xl border border-navy/12 bg-white px-4 py-3 text-left text-sm text-navy hover:border-gold hover:bg-gold/5 transition-colors disabled:opacity-50 ${className}`}
    >
      <span className="w-8 h-8 rounded-full bg-gold/12 flex items-center justify-center text-gold shrink-0">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : icon}
      </span>
      <span className="flex-1">{label}</span>
      {busy && <Check className="w-4 h-4 text-gold" />}
    </button>
  );
}
